import { zodResolver } from '@hookform/resolvers/zod';
import {
  CollaborationAccessLevelEnum,
  QuoteShareAccessRequest,
  QuoteShareAccessRequestSchema,
  QuoteStatusEnum,
  RecordShareEngagement,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { isValidDate } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { endOfDay } from 'date-fns/endOfDay';
import { parseISO } from 'date-fns/parseISO';
import isDate from 'lodash/isDate';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useCreateQuoteRecordShareAccess,
  useGetQuoteShareAccess,
  useRevokeQuoteRecordShare,
} from '../../../api/cpqService';
import {
  CAN_SHARE_QUOTE_EXTERNALLY_STATUSES,
  SHARE_QUOTE_EXTERNALLY_DRAFT_STATUSES,
} from '../../../constants/quotes';
import { useExperienceConfiguration } from '../../../hooks';
import { logger } from '../../../services/logger';

interface UseQuoteShareProps {
  quoteId: string;
  quoteExpirationDate: string;
  quoteStatus: QuoteStatusEnum;
  accessLevel: CollaborationAccessLevelEnum | null;
  engagements?: RecordShareEngagement[];
}

export const useQuoteShare = ({
  accessLevel,
  engagements,
  quoteExpirationDate,
  quoteId,
  quoteStatus,
}: UseQuoteShareProps) => {
  const { quoteShareConfig } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );

  const minExpiresAt = useMemo(() => endOfDay(addDays(new Date(), 1)), []);
  const maxExpiresAt = useMemo(() => {
    const date = parseISO(quoteExpirationDate);
    if (isValidDate(date)) {
      return endOfDay(date);
    }
    return endOfDay(addDays(new Date(), 30));
  }, [quoteExpirationDate]);

  const getQuoteShareQuery = useGetQuoteShareAccess(quoteId, {
    enabled:
      quoteShareConfig?.enabled &&
      accessLevel === CollaborationAccessLevelEnum.WRITE,
  });
  const addShareQuery = useCreateQuoteRecordShareAccess();
  const revokeShareQuery = useRevokeQuoteRecordShare();

  const formData = useForm<QuoteShareAccessRequest>({
    resolver: zodResolver(QuoteShareAccessRequestSchema),
    defaultValues: {
      quoteId,
      expiresAt: quoteExpirationDate,
    },
  });

  const hasWriteAccess = accessLevel === CollaborationAccessLevelEnum.WRITE;

  async function handleExternalShare(data: QuoteShareAccessRequest) {
    try {
      await addShareQuery.mutateAsync({
        ...data,
        expiresAt: endOfDay(
          isDate(data.expiresAt) ? data.expiresAt : parseISO(data.expiresAt),
        ),
      });
    } catch (ex) {
      handleApiErrorToast(ex);
      logger.warn('Error sharing quote', ex);
    }
  }

  const quoteShareRecords = getQuoteShareQuery.data?.length;

  // This looks complicated - but basically if quote is processed or cancelled only show this section if the have share access
  const externalShareInTerminalStateWithoutShareRecords =
    !new Set([
      ...CAN_SHARE_QUOTE_EXTERNALLY_STATUSES,
      ...SHARE_QUOTE_EXTERNALLY_DRAFT_STATUSES,
    ]).has(quoteStatus) &&
    !quoteShareRecords &&
    !engagements?.length;

  const externalShareState = {
    externalShareVisible:
      !!quoteShareConfig?.enabled &&
      !externalShareInTerminalStateWithoutShareRecords,
    canCreateShareLink:
      hasWriteAccess &&
      CAN_SHARE_QUOTE_EXTERNALLY_STATUSES.has(quoteStatus) &&
      !quoteShareRecords, // In future we will allow multiple external shares (backend allows it)
    showDraftMessage:
      hasWriteAccess && SHARE_QUOTE_EXTERNALLY_DRAFT_STATUSES.has(quoteStatus),
    hasRecordShareRecords: !!quoteShareRecords,
    showExternalActivity: !!engagements?.length || !!quoteShareRecords,
  };

  return {
    expires: {
      minExpiresAt,
      maxExpiresAt,
    },
    dataQuery: {
      getQuoteShareQuery,
      addShareQuery,
      revokeShareQuery,
    },
    formData,
    handleExternalShare,
    externalShareState,
  };
};
